import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Home, MessageSquare, Eye, Loader2, Phone,
  CheckCircle2, Star, Trash2, Crown, TrendingUp, Clock, Settings,
} from "lucide-react";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}><Icon className="w-5 h-5" /></div>
      <div className="text-2xl font-heading font-extrabold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("leads");

  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, enabled: !!user && user.role === "admin" });
  const leads = useQuery({ queryKey: ["admin-leads"], queryFn: async () => (await api.get("/admin/leads")).data, enabled: !!user && user.role === "admin" });
  const props = useQuery({ queryKey: ["admin-props"], queryFn: async () => (await api.get("/admin/properties")).data, enabled: !!user && user.role === "admin" });
  const subs = useQuery({ queryKey: ["admin-subs"], queryFn: async () => (await api.get("/admin/subscribers")).data, enabled: !!user && user.role === "admin" });
  const settingsQ = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/settings")).data.pricing, enabled: !!user && user.role === "admin" });
  const [pricing, setPricing] = useState(null);
  useEffect(() => { if (settingsQ.data && !pricing) setPricing(settingsQ.data); }, [settingsQ.data]); // eslint-disable-line

  if (loading) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const markLead = async (id) => { try { await api.post(`/admin/leads/${id}/status`, { status: "contacted" }); toast.success("Marked contacted"); leads.refetch(); } catch (e) { toast.error(apiError(e)); } };
  const updateProp = async (id, body) => { try { await api.post(`/admin/properties/${id}/status`, body); toast.success("Updated"); props.refetch(); stats.refetch(); } catch (e) { toast.error(apiError(e)); } };
  const delProp = async (id) => { try { await api.delete(`/admin/properties/${id}`); toast.success("Deleted"); props.refetch(); stats.refetch(); } catch (e) { toast.error(apiError(e)); } };
  const verifyProp = async (id) => { try { await api.post(`/admin/properties/${id}/verify`, { is_verified: true, notes: "Verified by Dotlands admin" }); toast.success("Marked Dotlands Verified"); props.refetch(); stats.refetch(); } catch (e) { toast.error(apiError(e)); } };
  const rejectProp = async (id) => { const reason = window.prompt("Reason for rejection (shown to owner)?") || ""; try { await api.post(`/admin/properties/${id}/reject`, { reason }); toast.success("Listing rejected"); props.refetch(); stats.refetch(); } catch (e) { toast.error(apiError(e)); } };
  const savePricing = async () => { try { await api.put("/admin/settings", { pricing }); toast.success("Prices updated"); settingsQ.refetch(); } catch (e) { toast.error(apiError(e)); } };

  const s = stats.data || {};

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center"><LayoutDashboard className="w-6 h-6 text-emerald-400" /></div>
        <div><h1 className="text-2xl font-heading font-bold text-slate-900">Owner Dashboard</h1><p className="text-sm text-slate-500">All leads & listings route to you</p></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={MessageSquare} label="New leads" value={s.new_leads ?? 0} color="bg-emerald-50 text-emerald-600" />
        <Stat icon={Home} label="Active listings" value={s.approved ?? 0} color="bg-blue-50 text-blue-600" />
        <Stat icon={Crown} label="Premium members" value={s.premium_users ?? 0} color="bg-amber-50 text-amber-600" />
        <Stat icon={Eye} label="Total views" value={s.total_views ?? 0} color="bg-purple-50 text-purple-600" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="leads" data-testid="tab-leads"><MessageSquare className="w-4 h-4 mr-1.5" /> Leads ({leads.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="listings" data-testid="tab-listings"><Home className="w-4 h-4 mr-1.5" /> Listings ({props.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="tab-subscribers"><Users className="w-4 h-4 mr-1.5" /> Premium ({subs.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="w-4 h-4 mr-1.5" /> Settings</TabsTrigger>
        </TabsList>

        {/* Leads */}
        <TabsContent value="leads">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {leads.isLoading ? <Center /> : (leads.data?.length ? (
              <div className="divide-y divide-slate-100">
                {leads.data.map((l) => (
                  <div key={l.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`lead-row-${l.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{l.name}</span>
                        {l.type === "direct_message" ? <Badge className="bg-amber-500">Direct (Premium)</Badge> : <Badge variant="secondary">Enquiry</Badge>}
                        {l.status === "new" && <Badge className="bg-emerald-600">New</Badge>}
                      </div>
                      <a href={`tel:${l.phone}`} className="text-sm text-emerald-600 font-medium flex items-center gap-1 mt-0.5"><Phone className="w-3.5 h-3.5" /> +91 {l.phone}</a>
                      <p className="text-sm text-slate-500 mt-1">On: <span className="text-slate-700 font-medium">{l.property_title}</span> · {l.property_price}</p>
                      {l.message && <p className="text-sm text-slate-600 mt-1 italic">"{l.message}"</p>}
                      <p className="text-xs text-slate-400 mt-1">{new Date(l.created_at).toLocaleString("en-IN")}</p>
                    </div>
                    {l.status === "new" && (
                      <Button size="sm" variant="outline" onClick={() => markLead(l.id)} data-testid={`mark-contacted-${l.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Mark contacted
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : <Empty text="No leads yet. They'll appear here when customers enquire." />)}
          </div>
        </TabsContent>

        {/* Listings */}
        <TabsContent value="listings">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {props.isLoading ? <Center /> : (props.data?.length ? (
              <div className="divide-y divide-slate-100">
                {props.data.map((p) => (
                  <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`admin-prop-${p.id}`}>
                    <img src={p.images?.[0]} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate">{p.title}</span>
                        {p.status === "sold" ? <Badge className="bg-red-600">Sold Out</Badge> : p.status === "rejected" ? <Badge className="bg-red-500">Rejected</Badge> : p.status === "approved" ? <Badge className="bg-emerald-600">Live</Badge> : <Badge className="bg-amber-500"><Clock className="w-3 h-3 mr-1" />Pending</Badge>}
                        {p.is_verified && <Badge className="bg-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Verified</Badge>}
                        {p.sold_requested && p.status !== "sold" && <Badge className="bg-amber-600">Sold requested</Badge>}
                        {p.featured && <Badge className="bg-amber-500"><Star className="w-3 h-3 mr-1" />Featured</Badge>}
                      </div>
                      <p className="text-sm text-slate-500">{p.price_display} · {p.locality}, {p.city} · by {p.owner_name}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0 justify-end">
                      {p.status !== "approved" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateProp(p.id, { status: "approved" })} data-testid={`approve-${p.id}`}>Approve</Button>}
                      {!p.is_verified && <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => verifyProp(p.id)} data-testid={`verify-${p.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Verify</Button>}
                      {p.status !== "rejected" && <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => rejectProp(p.id)} data-testid={`reject-${p.id}`}>Reject</Button>}
                      <Button size="sm" variant="outline" onClick={() => updateProp(p.id, { featured: !p.featured })} data-testid={`feature-${p.id}`}><Star className="w-4 h-4" /></Button>
                      {p.status !== "sold" && <Button size="sm" variant="outline" data-testid={`sold-${p.id}`} className={p.sold_requested ? "border-red-500 text-red-600" : ""} onClick={() => updateProp(p.id, { status: "sold" })}>{p.sold_requested ? "Confirm Sold" : "Mark Sold"}</Button>}
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => delProp(p.id)} data-testid={`delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <Empty text="No listings yet." />)}
          </div>
        </TabsContent>

        {/* Subscribers */}
        <TabsContent value="subscribers">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {subs.isLoading ? <Center /> : (subs.data?.length ? (
              <div className="divide-y divide-slate-100">
                {subs.data.map((u) => (
                  <div key={u.id} className="p-4 flex items-center gap-3" data-testid={`sub-row-${u.id}`}>
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Crown className="w-5 h-5 text-amber-600" /></div>
                    <div className="flex-1"><div className="font-semibold text-slate-900">{u.name}</div><div className="text-sm text-slate-500">+91 {u.phone}</div></div>
                    <Badge className={u.plan === "developer" ? "bg-emerald-600" : "bg-amber-500"}>{u.plan === "developer" ? "Developer" : "Premium"}</Badge>
                  </div>
                ))}
              </div>
            ) : <Empty text="No premium subscribers yet." />)}
          </div>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl">
            <h3 className="font-heading font-bold text-slate-900 mb-1">Pricing (₹)</h3>
            <p className="text-sm text-slate-500 mb-5">Edit listing promotion and agent subscription prices. Changes apply immediately.</p>
            {pricing ? (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[["featured", "Featured price"], ["featured_days", "Featured days"], ["premium", "Premium price"], ["premium_days", "Premium days"], ["super_premium", "Super Premium price"], ["super_premium_days", "Super Premium days"], ["agent", "Agent /month"], ["agent_pro", "Agent Pro /month"], ["agent_business", "Agent Business /month"], ["boost", "Boost price"]].map(([k, label]) => (
                    <div key={k}>
                      <label className="text-sm text-slate-600 mb-1 block">{label}</label>
                      <Input type="number" data-testid={`price-${k}`} value={pricing[k] ?? ""} onChange={(e) => setPricing({ ...pricing, [k]: Number(e.target.value) })} />
                    </div>
                  ))}
                </div>
                <Button className="mt-6 bg-emerald-600 hover:bg-emerald-700" onClick={savePricing} data-testid="save-pricing-btn">Save prices</Button>
              </>
            ) : <Center />}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Center = () => <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
const Empty = ({ text }) => <div className="text-center py-16 text-slate-400">{text}</div>;
